import React, { useCallback, useMemo, useRef } from "react";
import type { Identifier, XYCoord } from "dnd-core";
import { useDrag, useDrop } from "react-dnd";
import { ItemTypes, ItemTypeOne } from "./ItemTypes";
import { LABEL_MAP, appendFieldToItems, createFieldFromType } from "./Box";

const EmptyIllustration = () => (
  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#F9F0F4]">
    <svg
      width="32"
      height="32"
      viewBox="0 0 48 48"
      fill="none"
      aria-hidden
      className="text-[#841C39]"
    >
      <rect
        x="8"
        y="10"
        width="24"
        height="28"
        rx="3"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M14 18h12M14 24h12M14 30h8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M30 28l6 3-6 3v-2.2c-3.5.4-6 2.2-7.5 5.2.4-4.2 3.2-7.5 7.5-8.3V28z"
        fill="currentColor"
      />
    </svg>
  </div>
);

type Block =
  | { kind: "orphan"; fields: any[] }
  | { kind: "section"; section: any; fields: any[] };

/** Flat list → visual groups: fields after a section nest inside it. */
export const groupItemsIntoBlocks = (items: any[]): Block[] => {
  const list = Array.isArray(items) ? items : [];
  const blocks: Block[] = [];
  let orphan: any[] = [];
  let current: { kind: "section"; section: any; fields: any[] } | null = null;

  const flushOrphan = () => {
    if (orphan.length) {
      blocks.push({ kind: "orphan", fields: orphan });
      orphan = [];
    }
  };

  for (const item of list) {
    if (item?.type === "section") {
      flushOrphan();
      current = { kind: "section", section: item, fields: [] };
      blocks.push(current);
      continue;
    }
    if (current) {
      current.fields.push(item);
    } else {
      orphan.push(item);
    }
  }
  flushOrphan();
  return blocks;
};

const DragGrip = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="currentColor"
    className="text-[#9CA3AF]"
    aria-hidden
  >
    <circle cx="9" cy="6" r="1.5" />
    <circle cx="15" cy="6" r="1.5" />
    <circle cx="9" cy="12" r="1.5" />
    <circle cx="15" cy="12" r="1.5" />
    <circle cx="9" cy="18" r="1.5" />
    <circle cx="15" cy="18" r="1.5" />
  </svg>
);

interface SortDragItem {
  index: number;
  id: string;
  type: string;
}

/** Sortable field/section card — drag handle to reorder by flat index. */
const SortableFieldCard = ({
  card,
  index,
  selected,
  onEdit,
  onDelete,
  moveCard,
  insertPaletteAt,
}: {
  card: any;
  index: number;
  selected: boolean;
  onEdit: () => void;
  onDelete: () => void;
  moveCard: (dragIndex: number, hoverIndex: number) => void;
  insertPaletteAt: (fieldName: string, atIndex: number) => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const typeLabel = LABEL_MAP[card.type] || card.type;

  const [{ handlerId, isOverCard }, drop] = useDrop<
    SortDragItem | { name: string },
    { dropHandled?: boolean },
    { handlerId: Identifier | null; isOverCard: boolean }
  >(
    {
      accept: [ItemTypeOne.CARD, ItemTypes.BOX],
      collect(monitor) {
        return {
          handlerId: monitor.getHandlerId(),
          isOverCard: monitor.isOver({ shallow: true }),
        };
      },
      hover(item: any, monitor) {
        if (!ref.current) return;
        // Only reorder existing cards (not palette BOX)
        if (item?.name && !item?.id) return;
        const dragIndex = item.index;
        const hoverIndex = index;
        if (dragIndex === hoverIndex) return;

        const hoverBoundingRect = ref.current.getBoundingClientRect();
        const hoverMiddleY =
          (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
        const clientOffset = monitor.getClientOffset();
        if (!clientOffset) return;
        const hoverClientY =
          (clientOffset as XYCoord).y - hoverBoundingRect.top;

        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

        moveCard(dragIndex, hoverIndex);
        item.index = hoverIndex;
      },
      drop(item: any, monitor) {
        if (monitor.didDrop()) return undefined;
        // Palette component dropped onto this card → insert at this index
        if (item?.name && !item?.id) {
          insertPaletteAt(String(item.name), index);
          return { dropHandled: true };
        }
        return { dropHandled: true };
      },
    },
    [index, moveCard, insertPaletteAt]
  );

  const [{ isDragging }, drag, preview] = useDrag(
    {
      type: ItemTypeOne.CARD,
      item: () => ({ id: card.id, index }),
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    },
    [card.id, index]
  );

  preview(drop(ref));

  return (
    <div
      ref={ref}
      data-handler-id={handlerId}
      style={{ opacity: isDragging ? 0.35 : 1 }}
      className={`rounded-xl border-2 border-dashed bg-white px-3 py-3 transition ${
        selected
          ? "border-[#841C39] bg-[#F9F0F4]"
          : isOverCard
            ? "border-[#841C39] bg-[#F9F0F4]"
            : "border-[#93C5FD]"
      }`}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <button
          type="button"
          ref={drag as any}
          title="Drag to reorder"
          className="mt-0.5 flex h-7 w-7 shrink-0 cursor-grab items-center justify-center rounded-lg text-[#9CA3AF] hover:bg-[#F3F4F6] active:cursor-grabbing"
        >
          <DragGrip />
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold capitalize text-[#111827]">
            {card.name || typeLabel}
          </p>
          <p className="text-[11px] text-[#6B7280]">
            {typeLabel}
            {card.required ? " · required" : ""}
            <span className="ml-1 text-[#D1D5DB]">· #{index + 1}</span>
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            title="Edit"
            onClick={onEdit}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-[#3B82F6] transition hover:bg-[#EFF6FF]"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
          </button>
          <button
            type="button"
            title="Delete"
            onClick={onDelete}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-[#EF4444] transition hover:bg-[#FEF2F2]"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
            </svg>
          </button>
        </div>
      </div>
      <div className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2 text-xs text-[#9CA3AF]">
        {card.type === "textarea" || card.type === "address"
          ? card.placeholder || "Multi-line text"
          : card.type === "checkbox" || card.type === "radio"
          ? (Array.isArray(card.options)
              ? card.options
              : String(card.options || "")
                  .split(";")
                  .filter(Boolean)
            ).join(" · ") || "Options"
          : card.type === "project"
          ? `${card.projectScope === "selected" ? "Selected" : "All"} projects · ${
              card.selectionMode === "multiple" ? "Multiple" : "Single"
            }`
          : card.type === "select"
          ? "Select…"
          : card.type === "section"
          ? card.sectionType === "step"
            ? "Form step"
            : "Section group"
          : card.placeholder || typeLabel}
      </div>
    </div>
  );
};

const SectionDropZone = ({
  sectionId,
  sectionIndex,
  children,
  isEmpty,
  setItems,
  insertPaletteAt,
}: {
  sectionId: string;
  sectionIndex: number;
  children: React.ReactNode;
  isEmpty: boolean;
  setItems: (updater: any) => void;
  insertPaletteAt: (fieldName: string, atIndex: number) => void;
}) => {
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: ItemTypes.BOX,
      drop: (item: { name?: string }, monitor: any) => {
        if (monitor.didDrop()) return undefined;
        const fieldName = String(item?.name || "");
        if (!fieldName) return undefined;
        if (fieldName === "section") {
          // New section after this one
          insertPaletteAt("section", sectionIndex + 1);
        } else {
          // Insert as first child after section marker
          insertPaletteAt(fieldName, sectionIndex + 1);
        }
        return {
          allowedDropEffect: "any",
          dropEffect: "move",
          sectionId,
          dropHandled: true,
        };
      },
      collect: (monitor: any) => ({
        isOver: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      }),
    }),
    [sectionId, sectionIndex, insertPaletteAt, setItems]
  );

  return (
    <div
      ref={drop as any}
      className={`min-h-[72px] space-y-2.5 p-3 transition ${
        isOver && canDrop ? "bg-[#DBEAFE]" : "bg-white"
      }`}
    >
      {children}
      {isEmpty ? (
        <p
          className={`rounded-lg border border-dashed px-3 py-6 text-center text-xs ${
            isOver && canDrop
              ? "border-[#3B82F6] text-[#1D4ED8]"
              : "border-[#BFDBFE] text-[#60A5FA]"
          }`}
        >
          {isOver && canDrop
            ? "Release to add field inside this section"
            : "Drop fields here · drag ⋮⋮ handle to reorder"}
        </p>
      ) : isOver && canDrop ? (
        <p className="text-center text-xs font-semibold text-[#1D4ED8]">
          Release to add inside this section
        </p>
      ) : null}
    </div>
  );
};

export const Dustbin = (props: any) => {
  const items: any[] = Array.isArray(props?.items) ? props.items : [];

  const moveCard = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      if (dragIndex === hoverIndex) return;
      props.setItems((prev: any[]) => {
        const list = Array.isArray(prev) ? [...prev] : [];
        if (
          dragIndex < 0 ||
          hoverIndex < 0 ||
          dragIndex >= list.length ||
          hoverIndex >= list.length
        ) {
          return list;
        }
        const [removed] = list.splice(dragIndex, 1);
        list.splice(hoverIndex, 0, removed);
        return list;
      });
    },
    [props.setItems]
  );

  const insertPaletteAt = useCallback(
    (fieldName: string, atIndex: number) => {
      props.setItems((prev: any[]) => {
        const list = Array.isArray(prev) ? [...prev] : [];
        const newItem = createFieldFromType(fieldName);
        const idx = Math.max(0, Math.min(atIndex, list.length));
        // Sections always append as new group unless inserting as section
        if (fieldName === "section") {
          list.splice(idx, 0, newItem);
          return list;
        }
        list.splice(idx, 0, newItem);
        return list;
      });
    },
    [props.setItems]
  );

  const [{ canDrop, isOver }, drop] = useDrop(
    () => ({
      accept: ItemTypes.BOX,
      drop: (item: { name?: string }, monitor: any) => {
        if (monitor.didDrop()) return undefined;
        const fieldName = String(item?.name || "");
        if (fieldName) {
          props.setItems((prev: any[]) => appendFieldToItems(prev, fieldName));
        }
        return {
          name: `${props?.allowedDropEffect || "any"} Dustbin`,
          allowedDropEffect: props?.allowedDropEffect || "any",
          dropEffect: "move",
          dropHandled: true,
        };
      },
      collect: (monitor: any) => ({
        isOver: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      }),
    }),
    [props?.allowedDropEffect, props?.setItems]
  );

  const handleDelete = (id: any) => {
    props.setItems(items.filter((item: any) => item.id !== id));
  };

  const handleDeleteSection = (sectionId: string) => {
    props.setItems(items.filter((item: any) => item.id !== sectionId));
  };

  const flatIndexById = useCallback(
    (id: string) => items.findIndex((it) => it?.id === id),
    [items]
  );

  const isActive = canDrop && isOver;
  const hasItems = items.length > 0;
  const blocks = useMemo(() => groupItemsIntoBlocks(items), [items]);

  return (
    <div
      ref={drop as any}
      className={`flex min-h-[280px] flex-col overflow-y-auto overflow-x-hidden rounded-xl border-2 border-dashed p-5 transition ${
        isActive
          ? "border-[#841C39] bg-[#F9F0F4]"
          : canDrop
            ? "border-[#A5B4FC] bg-[#F8FAFF]"
            : "border-[#E5E7EB] bg-[#FAFBFC]"
      }`}
    >
      {!hasItems ? (
        <div className="pointer-events-none flex flex-1 flex-col items-center justify-center px-4 py-8 text-center">
          <EmptyIllustration />
          <p className="text-sm font-bold text-[#111827]">
            {isActive ? "Release to drop" : "Drag & Drop Components Here."}
          </p>
          <p className="mt-1 max-w-xs text-xs text-[#6B7280]">
            Drop from the left, or click a component to add. Then drag ⋮⋮ to
            reorder.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-[11px] text-[#6B7280]">
            Drag the{" "}
            <span className="inline-flex align-middle">
              <DragGrip />
            </span>{" "}
            handle to reorder fields · drop from left to insert
          </p>
          {isActive ? (
            <p className="mb-1 text-center text-sm font-semibold text-[#841C39]">
              Release to append at the end
            </p>
          ) : null}

          {blocks.map((block, bi) => {
            if (block.kind === "orphan") {
              return (
                <div key={`orphan-${bi}`} className="flex flex-col gap-2.5">
                  {block.fields.map((card: any) => {
                    const idx = flatIndexById(card.id);
                    return (
                      <SortableFieldCard
                        key={card.id}
                        card={card}
                        index={idx}
                        selected={props.inputId === card.id}
                        onEdit={() => props.setInputId(card.id)}
                        onDelete={() => handleDelete(card.id)}
                        moveCard={moveCard}
                        insertPaletteAt={insertPaletteAt}
                      />
                    );
                  })}
                </div>
              );
            }

            const card = block.section;
            const sectionIndex = flatIndexById(card.id);

            return (
              <div
                key={card.id ?? bi}
                className="overflow-hidden rounded-xl border-2 border-[#3B82F6]"
              >
                <div className="bg-[#EFF6FF] p-2">
                  <SortableFieldCard
                    card={card}
                    index={sectionIndex}
                    selected={props.inputId === card.id}
                    onEdit={() => props.setInputId(card.id)}
                    onDelete={() => handleDeleteSection(card.id)}
                    moveCard={moveCard}
                    insertPaletteAt={insertPaletteAt}
                  />
                </div>

                <SectionDropZone
                  sectionId={card.id}
                  sectionIndex={sectionIndex}
                  isEmpty={block.fields.length === 0}
                  setItems={props.setItems}
                  insertPaletteAt={insertPaletteAt}
                >
                  {block.fields.map((field: any) => {
                    const idx = flatIndexById(field.id);
                    return (
                      <SortableFieldCard
                        key={field.id}
                        card={field}
                        index={idx}
                        selected={props.inputId === field.id}
                        onEdit={() => props.setInputId(field.id)}
                        onDelete={() => handleDelete(field.id)}
                        moveCard={moveCard}
                        insertPaletteAt={insertPaletteAt}
                      />
                    );
                  })}
                </SectionDropZone>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
